import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { StarInput } from "@/components/Stars";

const VENDOR_DIMS = [
  { key: "punctuality", label: "Punctuality", hint: "Arrived on time and set up promptly" },
  { key: "communication", label: "Communication", hint: "Responsive and clear before / during the event" },
  { key: "cleanliness", label: "Cleanliness", hint: "Left the booth area clean" },
  { key: "booth", label: "Booth presentation", hint: "Booth looked professional and inviting" },
  { key: "product", label: "Product quality", hint: "Products matched their application and were high-quality" },
];

const ORGANIZER_DIMS = [
  { key: "ease_of_finding", label: "Ease of locating event", hint: "Was signage / directions clear?" },
  { key: "attendees", label: "Attendees", hint: "Foot traffic and crowd energy" },
  { key: "sales", label: "Sales", hint: "How did revenue land vs. your expectations" },
  { key: "location", label: "Location", hint: "Venue vibe, layout, indoor/outdoor conditions" },
  { key: "parking", label: "Parking", hint: "Load-in access and vendor parking" },
  { key: "organizer_communication", label: "Organizer communication", hint: "Timely, clear, kind before and during the event" },
];

export default function RatingDialog({
  applicationId,
  direction,           // 'vendor' → organizer rating vendor  |  'organizer' → vendor rating organizer
  counterpartyName,
  eventTitle,
  trigger,
  onRated,
}) {
  const [open, setOpen] = useState(false);
  const [scores, setScores] = useState({});
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const dims = direction === "vendor" ? VENDOR_DIMS : ORGANIZER_DIMS;
  // Vendor→organizer ratings can skip categories the vendor has no opinion on
  // (e.g. biked in → no parking). Organizer→vendor ratings still require all.
  const requireAll = direction === "vendor";
  const setCount = dims.filter((d) => scores[d.key] >= 1 && scores[d.key] <= 5).length;
  const canSubmit = requireAll ? setCount === dims.length : setCount >= 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(requireAll
        ? "Please rate every category"
        : "Rate at least one category so this review has meaning");
      return;
    }
    setBusy(true);
    try {
      const endpoint = direction === "vendor" ? "/ratings/vendor" : "/ratings/organizer";
      // Send only the categories actually rated so the backend can distinguish
      // "no opinion" from "zero stars" (they mean very different things).
      const rated = Object.fromEntries(
        dims.filter((d) => scores[d.key]).map((d) => [d.key, scores[d.key]])
      );
      const payload = { application_id: applicationId, comment, ...rated };
      const { data } = await api.post(endpoint, payload);
      toast.success(`Thanks — you rated ${counterpartyName} ${data.overall}/5`);
      setOpen(false);
      setScores({});
      setComment("");
      onRated && onRated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit rating");
    } finally { setBusy(false); }
  };

  const title = direction === "vendor" ? "Rate this vendor" : "Rate this event";
  const desc = direction === "vendor"
    ? `Share how ${counterpartyName} did at ${eventTitle}. Ratings are visible on their public profile.`
    : `How was ${eventTitle}? Rate the categories that matter to you — skip any you don't have an opinion on.`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="rating-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          {dims.map((d) => (
            <div key={d.key} className="flex items-start justify-between gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">{d.label}</Label>
                <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>{d.hint}</div>
              </div>
              <StarInput
                value={scores[d.key] || 0}
                onChange={(v) => setScores({ ...scores, [d.key]: v })}
                testId={`rating-${d.key}`}
              />
            </div>
          ))}
          <div>
            <Label className="text-xs uppercase tracking-widest">Comment (optional)</Label>
            <Textarea
              data-testid="rating-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Anything future partners should know…"
            />
          </div>
          <Button
            data-testid="rating-submit"
            type="submit"
            disabled={busy || !canSubmit}
            className="btn-primary w-full py-5 rounded-full inline-flex items-center justify-center gap-2"
          >
            <Star className="h-4 w-4" /> {busy ? "Submitting…" : "Submit rating"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
